import React, { useRef, useState } from 'react';
import Image from '../../../../../components/elements/Image';
import cookie from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import FormButton from '../shared/FormButton';
import Radio from '../shared/Radio';
import styles from './PaymentDetails.module.css';

import {
  selectPaymentType,
  selectPlanDetails,
  selectPlans,
  selectSendingDetails,
  selectSpecial,
  selectTrialInDays,
  selectUserDetails
} from '../../../store/selectors';
import {
  sendPaymentDetailsForm,
  changeSelectedPaymentType,
} from '../../../store';

const stripeElementStyle = {
  style: {
    base: {
      fontSize: '18px',
      lineHeight: '24px',
      color: '#495057',
    },
  },
};

const PaymentDetails = ({
  plan,
  forceTrialInDays,
  hideAnnual,
  couponId,
  confirmTitle,
  funnel,
  hidePlanDescription = false,
}) => {
  const [isFirstRun, setIsFirstRun] = useState(true);
  // const [cardholderName, setCardholderName] = useState('');
  const [cardNumberError, setCardNumberError] = useState('');
  const [stripeLoading, setStripeLoading] = useState(false);

  const sendingPaymentDetailsForm = useSelector(selectSendingDetails);
  const paymentType = useSelector(selectPaymentType);
  const plans = useSelector(selectPlans);
  const planDetails = useSelector(selectPlanDetails);
  const isSpecial = useSelector(selectSpecial);
  const popupRef = useRef();
  const trialInDays = useSelector(selectTrialInDays);
  const userDetails = useSelector(selectUserDetails);

  const dispatch = useDispatch();

  const stripe = useStripe();
  const elements = useElements();

  const sendPaymentDetailsFormAttempt = async (e) => {
    e.preventDefault();

    setStripeLoading(true);
    if (!stripe || !elements) return;
    setIsFirstRun(false);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    const paymentMethodResponse = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    // const { error, token } = await stripe.createToken(cardElement, { name: cardholderName });
    const { error, token } = await stripe.createToken(cardElement);

    setStripeLoading(false);

    if (paymentMethodResponse.error || error) {
      setCardNumberError(
        paymentMethodResponse.error?.message || error?.message || ''
      );
      return;
    }

    dispatch(
      sendPaymentDetailsForm({
        plan: isSpecial ? planDetails?.plan : plan,
        coupon_id: couponId ?? null,
        paymentType: paymentType || 'month',
        stripeToken: token?.id,
        card_last4: token?.card?.last4,
        card_brand: token?.card?.brand,
        trial_days: forceTrialInDays ? trialInDays : null,
        paymentMethodId: paymentMethodResponse.paymentMethod?.id,
        fbc: cookie.get('_fbc'),
        fbp: cookie.get('_fbp'),
        funnel: funnel ?? 'cc',
        email: userDetails.user.email
      })
    );
  };

  const onChangeCardNumber = (event) => {
    setCardNumberError(
      event.error?.message ||
        (event.complete ? '' : 'Please fill out all fields')
    );
  };

  const onChangePaymentType = (type) => {
    dispatch(changeSelectedPaymentType(type));
  };

  if (!isSpecial && !plans) {
    return '';
  }

  return (
    <div className={styles.paymentFormContainer}>
      <div className={styles.paymentFormContainerInner}>
        <h3 className={styles.rightTitle}>
          2. {confirmTitle ? confirmTitle : `Confirm Your Trial`}
        </h3>
        <div className="d-flex align-items-center">
          <div className={styles.stepText}>Step 2 of 2</div>
          <Image name="step-two-gauge" className={styles.stepTwoImage} />
        </div>
      </div>
      <form onSubmit={sendPaymentDetailsFormAttempt} id="subscription-form">
        {isSpecial && (
          <div
            className={`${styles.formContainer} ${styles.specialFormContainer}`}
          >
            <h3 className={styles.rightTitleMobile}>2. Confirm Your Trial</h3>
            <div className={`${styles.planContainer}`}>
              <div
                className={`${styles.selectPlan} ${styles.selectSpecialPlan} ${styles.selectedArea}`}
                aria-hidden="true"
                role="button"
              >
                <div className="d-flex align-items-end">
                  <Radio
                    id="ch-annual"
                    checked
                    text={planDetails.planName}
                    name="planType"
                    value={planDetails.paymentType}
                    role="button"
                  />
                  <div className={styles.planPrice}></div>
                </div>
                <p className={styles.smallSpecialPlanText}>
                  Billed {planDetails.price} after a {planDetails.trialInDays}
                  -day trial.
                </p>
              </div>
            </div>
          </div>
        )}
        {!isSpecial && (
          <div className={styles.formContainer}>
            <h3 className={styles.rightTitleMobile}>2. Confirm Your Trial</h3>
            <p className={styles.subheadlineFirst}>Select Your Billing Cycle</p>
            <div className={`${styles.planContainer}`}>
              {!hideAnnual && (
                <div
                  className={`${styles.selectPlan} ${
                    'annually' === paymentType ? styles.selectedArea : ''
                  }`}
                  onClick={() => onChangePaymentType('annually')}
                  aria-hidden="true"
                  role="button"
                >
                  <div className={styles.freeMonthStamp}>
                    <Image
                      name="free_months"
                      className={styles.freeMonthImage}
                    />
                  </div>
                  <div className="d-flex align-items-end">
                    <Radio
                      id="ch-annual"
                      checked={'annually' === paymentType}
                      onChange={(e) => onChangePaymentType(e.target.value)}
                      text="Annual"
                      name="planType"
                      value="annually"
                      role="button"
                    />
                    <div className={styles.planPrice}>
                      {' '}
                      - {plans[plan].price['annually']}
                    </div>
                  </div>
                  <p className={styles.smallPlanText}>
                    Billed annually for ${plans[plan].price['annually_price']}
                  </p>
                </div>
              )}

              <div
                className={`${styles.selectPlan} ${
                  'month' === paymentType ? styles.selectedArea : ''
                }`}
                onClick={() => onChangePaymentType('month')}
                aria-hidden="true"
                role="button"
              >
                <div className="d-flex align-items-end">
                  <Radio
                    id="ch-month"
                    checked={'month' === paymentType}
                    onChange={(e) => onChangePaymentType(e.target.value)}
                    text="Monthly"
                    name="planType"
                    value="month"
                    role="button"
                  />

                  <div className={styles.planPrice}> - {plans[plan].price['month']}</div>
                </div>
                <p className={styles.smallPlanText}>
                  Billed monthly for ${plans[plan].price['month_price']}
                </p>
              </div>
            </div>
          </div>
        )}
        <div className={`${styles.detailsContainer}`}>
          <div className={styles.paymentDetails}>
            <p className={styles.subheadline}>Enter Your Payment Details</p>
            <div className="d-flex justify-content-center align-items-center">
              <Image name="cc_jcb" className={styles.cc1} />
              <Image name="cc_mc" className={styles.cc1} />
              <Image name="cc_ae" className={styles.cc2} />
              <Image name="cc_visa" className={styles.cc2} />
              <Image name="cc_dis" className={styles.cc2} />
            </div>
          </div>
        </div>
        <div className={`${styles.formControl} row`}>
          <div className="col-12">
            <CardElement
              onChange={onChangeCardNumber}
              className={`${styles.stripeElement} ${
                !isFirstRun && cardNumberError ? styles.error : ''
              }`}
              options={stripeElementStyle}
              role="textbox"
            />
            {!isFirstRun && cardNumberError && (
              <div className={styles.validationErrorText}>
                {cardNumberError}
              </div>
            )}
          </div>
        </div>
        <div className={styles.bottomText}>
          <p>
            By clicking Start Boosting Sales Now!, I agree to the{' '}
            <a
              rel="noopener noreferrer"
              target="_blank"
              href="https://www.deadlinefunnel.com/tos"
            >
              Terms and Privacy Policy
            </a>
          </p>
          {!hidePlanDescription && (
            <p>
              After your trial you'll be charged{' '}
              {isSpecial
                ? planDetails?.price
                : plans?.[plan]?.price[paymentType]}
              .{' '}
              <div
                className={styles.popup}
                onMouseEnter={(e) => {
                  popupRef.current.style.display = 'block';
                }}
                onMouseLeave={(e) => {
                  popupRef.current.style.display = 'none';
                }}
                role="presentation"
              >
                <span className={styles.link}>Read more</span>
                <div className={styles.popupContainer} ref={popupRef}>
                  <div className={styles.popupInnerText}>
                    We ask for your credit card to ensure work continuity after
                    your trial expires. Your credit card will not be charged at
                    any point during the 14 days of the trial. Your subscription
                    will automatically start at the end of the trial with the
                    plan you selected and you will be able to upgrade to a
                    higher plan at any time. If you decide that Deadline Funnel
                    isn’t for you, you can cancel whenever you want to, both
                    during and after your trial.
                  </div>
                  <Image
                    name="signup-polygon"
                    className={styles.popupPolygon}
                  />
                </div>
              </div>
            </p>
          )}
        </div>
        <FormButton
          submitting={sendingPaymentDetailsForm || stripeLoading}
          submittingText="Creating your account..."
          className={styles.sendPaymentDetailsButton}
        >
          START BOOSTING SALES NOW!
        </FormButton>
      </form>
    </div>
  );
};

export default PaymentDetails;
